import { withCors } from './utils/cors';

function normaliseApiBase(base) {
  if (!base) return null;

  const trimmed = base.replace(/\/+$/, '');
  if (!trimmed) {
    return null;
  }

  // Keep explicit API version as-is.
  if (/\/api\/v1$/i.test(trimmed)) {
    return trimmed;
  }

  // If only /api is provided, append /v1.
  if (/\/api$/i.test(trimmed)) {
    return `${trimmed}/v1`;
  }

  // If caller intentionally provides /v1 root, keep it.
  if (/\/v1$/i.test(trimmed)) {
    return trimmed;
  }

  // Default to Laravel-style /api/v1.
  return `${trimmed}/api/v1`;
}

// Determine API base URLs.
// Prefer explicit environment variables for each subsystem, but fall back
// to the GUHSO API so local development works without additional
// configuration.
const DEFAULT_API_BASE = process.env.REACT_APP_API_URL || 'https://guhso.com/api';

export const API_URL = normaliseApiBase(DEFAULT_API_BASE);
export const OT_API_URL =
  normaliseApiBase(process.env.REACT_APP_OT_API_URL) || API_URL;
export const PROFILE_API_URL =
  normaliseApiBase(process.env.REACT_APP_PROFILE_API_URL) || API_URL;

export const makeCorsUrl = (baseUrl, path) => withCors(`${baseUrl}${path}`);

const corsUrl = (path) => makeCorsUrl(API_URL, path);
export const AUTH_TOKEN_STORAGE_KEY = 'guhso-auth-token';
const AUTH_TOKEN_EXPIRY_STORAGE_KEY = 'guhso-auth-token-expiry';
const AUTH_TOKEN_TTL_MS = 1000 * 60 * 60 * 12; // 12 hours

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
};

export const getStoredAuthToken = () => {
  const storage = getStorage();
  if (!storage) return null;

  const token = storage.getItem(AUTH_TOKEN_STORAGE_KEY);
  const expiry = Number(storage.getItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY));

  if (!token) {
    return null;
  }

  if (!expiry || Number.isNaN(expiry) || Date.now() >= expiry) {
    storage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    storage.removeItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY);
    return null;
  }

  return token;
};

export const setStoredAuthToken = (token) => {
  const storage = getStorage();
  if (!storage) return;

  if (token) {
    storage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    storage.setItem(
      AUTH_TOKEN_EXPIRY_STORAGE_KEY,
      String(Date.now() + AUTH_TOKEN_TTL_MS)
    );
    return;
  }

  storage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  storage.removeItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY);
};

const buildAuthHeaders = (headers = {}) => {
  const token = getStoredAuthToken();

  return token
    ? {
        ...headers,
        'Authorization': `Bearer ${token}`,
      }
    : headers;
};

/**
 * Process episode data to normalize thumbnail fields
 * @param {Object} episode - Raw episode data from API
 * @returns {Object} Processed episode data
 */
const processEpisodeData = (episode) => {
  return {
    ...episode,
    // Normalize thumbnail fields for our thumbnail system
    originalThumbnail: episode.thumbnail_url || episode.itunes_image,
    // Add slug generation if not provided
    slug: episode.slug || generateSlugFromTitle(episode.title),
  };
};

/**
 * Generate URL-friendly slug from episode title
 * @param {string} title - Episode title
 * @returns {string} URL-friendly slug
 */
const generateSlugFromTitle = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .slice(0, 50); // Limit length
};

// Episodes API
export async function fetchEpisodes() {
  const res = await fetch(corsUrl('/episodes'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch episodes: ${res.status}`);
  }
  const data = await res.json();
  
  // Process episodes to normalize thumbnail data
  if (Array.isArray(data.data)) {
    data.data = data.data.map(processEpisodeData);
  } else if (Array.isArray(data)) {
    return data.map(processEpisodeData);
  }
  
  return data;
}

/**
 * Site search. Backed by GET /search, which returns { shows, episodes, total }.
 *
 * @param {string} query
 * @param {{type?: 'all'|'shows'|'episodes', limit?: number, signal?: AbortSignal}} options
 */
export async function searchContent(query, { type = 'all', limit = 8, signal } = {}) {
  const trimmed = (query || '').trim();

  if (!trimmed) {
    return { shows: [], episodes: [], total: 0 };
  }

  const params = new URLSearchParams({ q: trimmed, type, limit: String(limit) });
  const res = await fetch(corsUrl(`/search?${params.toString()}`), {
    headers: buildAuthHeaders(),
    signal,
  });

  if (!res.ok) {
    throw new Error(`Search failed: ${res.status}`);
  }

  const data = await res.json();

  return {
    shows: Array.isArray(data.shows) ? data.shows : [],
    episodes: Array.isArray(data.episodes) ? data.episodes : [],
    total: Number(data.total) || 0,
  };
}

export async function fetchHeroEpisode() {
  const res = await fetch(corsUrl('/featured/episode'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch hero episode: ${res.status}`);
  }
  const data = await res.json();
  
  // Process hero episode data
  return processEpisodeData(data);
}


// Categories API — real name/color per category (used by the Sonic Motion
// footer's "Browse by Category" chips).
export async function fetchCategories() {
  const res = await fetch(corsUrl('/categories'));
  if (!res.ok) {
    throw new Error(`Failed to fetch categories: ${res.status}`);
  }
  return res.json();
}

// Advertisements API
export async function fetchActiveAds() {
  const res = await fetch(corsUrl('/ads'));
  if (!res.ok) {
    throw new Error(`Failed to fetch ads: ${res.status}`);
  }
  return res.json();
}

export async function fetchShorts() {
  const res = await fetch(corsUrl('/shorts'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ visitorToken: getShortVisitorToken() }),
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch Shorts: ${res.status}`);
  }
  const payload = await res.json();
  return Array.isArray(payload) ? payload : (payload?.data || []);
}

const SHORT_VISITOR_STORAGE_KEY = 'guhso-short-visitor';

function getShortVisitorToken() {
  if (typeof window === 'undefined') return 'server-rendered-visitor';

  let token = window.localStorage.getItem(SHORT_VISITOR_STORAGE_KEY);
  if (!token) {
    token = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(SHORT_VISITOR_STORAGE_KEY, token);
  }
  return token;
}

function shortVisitorHeaders(headers = {}) {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...headers,
  };
}

async function shortRequest(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const visitorToken = getShortVisitorToken();
  const requestPath = path;
  const body = method === 'GET'
    ? options.body
    : JSON.stringify({ ...(options.body ? JSON.parse(options.body) : {}), visitorToken });
  const response = await fetch(corsUrl(requestPath), {
    ...options,
    body,
    headers: shortVisitorHeaders(options.headers),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const validationMessage = payload?.errors ? Object.values(payload.errors).flat()[0] : null;
    throw new Error(validationMessage || payload?.message || 'The request could not be completed.');
  }
  return payload;
}

export function toggleShortLike(shortId) {
  return shortRequest(`/shorts/${shortId}/like`, { method: 'POST', body: '{}' });
}

export async function fetchShortComments(shortId) {
  const payload = await shortRequest(`/shorts/${shortId}/comments`);
  return payload?.data || [];
}

export function postShortComment(shortId, values) {
  return shortRequest(`/shorts/${shortId}/comments`, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify(values),
  });
}

export function recordShortShare(shortId, channel) {
  return shortRequest(`/shorts/${shortId}/share`, { method: 'POST', body: JSON.stringify({ channel }) });
}

// Posts API (keeping existing functionality)
export async function fetchFeaturedPosts() {
  const res = await fetch(corsUrl('/featured/episodes'));
  return res.json();
}

export async function fetchPosts() {
  const res = await fetch(corsUrl('/posts'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch posts: ${res.status}`);
  }
  return res.json();
}

export async function fetchPostBySlug(slug) {
  const res = await fetch(corsUrl(`/posts/${slug}`), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch post: ${res.status}`);
  }
  return res.json();
}

// Products API
export async function fetchProducts() {
  const res = await fetch(corsUrl('/products'));
  if (!res.ok) {
    throw new Error(`Failed to fetch products: ${res.status}`);
  }
  return res.json();
}

export async function fetchHomepageSponsorProducts() {
  const res = await fetch(corsUrl('/products/homepage-sponsors'));
  if (!res.ok) {
    throw new Error(`Failed to fetch homepage sponsor products: ${res.status}`);
  }
  return res.json();
}

export async function fetchProductBySlug(slug) {
  const res = await fetch(corsUrl(`/products/${slug}`));
  if (!res.ok) {
    throw new Error(`Failed to fetch product: ${res.status}`);
  }
  return res.json();
}

export async function fetchHeroImages() {
  const res = await fetch(corsUrl('/products/hero-images'));
  if (!res.ok) {
    throw new Error(`Failed to fetch hero images: ${res.status}`);
  }
  return res.json();
}

// Mailing list subscription
export async function subscribeToMailingList(first_name, email) {
  const normalisedName = (first_name || '').trim();
  const normalisedEmail = (email || '').trim().toLowerCase();

  if (!normalisedEmail) {
    throw new Error('A valid email address is required to subscribe.');
  }

  const payload = {
    first_name: normalisedName,
    firstName: normalisedName,
    email: normalisedEmail,
    email_address: normalisedEmail,
    source: 'web',
  };

  const res = await fetch(corsUrl('/mailing-list'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const raw = await res.text();
    let message = `Subscription failed (${res.status}).`;

    try {
      const parsed = raw ? JSON.parse(raw) : null;
      const apiMessage = parsed?.message;
      const emailError = parsed?.errors?.email?.[0];
      message = emailError || apiMessage || message;
    } catch {
      if (raw) message = raw;
    }

    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  // Some deployments respond with HTTP 204 (No Content) on success. Attempting
  // to parse JSON from an empty body throws, which previously surfaced as a
  // failure in the UI even though the subscription completed successfully.
  if (res.status === 204) {
    return null;
  }

  const text = await res.text();
  if (!text) {
    return null;
  }

  const contentType = res.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return JSON.parse(text);
  }

  try {
    return JSON.parse(text);
  } catch (err) {
    // Some integrations (notably newsletter providers) return a bare string or
    // HTML snippet on success. Treat those as successful responses instead of
    // bubbling up a parsing error that would surface as a failure message in
    // the UI.
    return text;
  }
}

export async function fetchDonationCards() {
  const res = await fetch(corsUrl('/donation/cards'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch donation cards: ${res.status}`);
  }
  return res.json();
}

export async function fetchMembershipPlans() {
  const res = await fetch(corsUrl('/membership/plans'), {
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch membership plans: ${res.status}`);
  }

  return res.json();
}

export async function createMembershipCheckout(payload) {
  const res = await fetch(corsUrl('/membership/checkout'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new Error(body?.message || `Membership checkout failed (${res.status})`);
  }

  return body;
}

export async function createCartCheckout(payload) {
  const res = await fetch(corsUrl('/checkout'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new Error(body?.message || `Checkout failed (${res.status})`);
  }

  return body;
}

// Backwards-compatible name for any older callers outside the main app bundle.
export const createMerchCheckout = createCartCheckout;

export async function recordDonationTransaction(payload) {
  const res = await fetch(corsUrl('/donations/record'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const raw = await res.text();
    throw new Error(raw || `Failed to record donation transaction (${res.status})`);
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// Tech API
export async function fetchTechArticles() {
  const res = await fetch(corsUrl('/tech'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch tech articles: ${res.status}`);
  }
  return res.json();
}

export async function fetchFeaturedTechArticles() {
  const res = await fetch(corsUrl('/tech/featured'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch featured tech articles: ${res.status}`);
  }
  return res.json();
}

export async function fetchStickyTechArticles() {
  const res = await fetch(corsUrl('/tech/sticky'), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch sticky tech articles: ${res.status}`);
  }
  return res.json();
}

export async function fetchTechArticleBySlug(slug) {
  const res = await fetch(corsUrl(`/tech/${slug}`), {
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch tech article: ${res.status}`);
  }
  return res.json();
}

export async function fetchAccount() {
  const res = await fetch(corsUrl('/user/account'), {
    headers: buildAuthHeaders({
      'Accept': 'application/json',
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch account: ${res.status}`);
  }

  return res.json();
}

export async function fetchAccessState() {
  const res = await fetch(corsUrl('/user/access'), {
    headers: buildAuthHeaders({
      'Accept': 'application/json',
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch access state: ${res.status}`);
  }

  return res.json();
}

// ---------------------------------------------------------------------------
// Directory advertising (paid placements)
// ---------------------------------------------------------------------------

export async function fetchDirectoryPackages() {
  const res = await fetch(corsUrl('/directory/packages'), {
    headers: { 'Accept': 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch advertising packages: ${res.status}`);
  }

  return res.json();
}

export async function fetchDirectoryInventory(params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => Boolean(value))
  ).toString();

  const res = await fetch(corsUrl(`/directory/inventory${query ? `?${query}` : ''}`), {
    headers: { 'Accept': 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch placement availability: ${res.status}`);
  }

  return res.json();
}

// The server prices every campaign from the package record, so nothing about the
// amount charged is taken from this payload.
export async function createDirectoryCheckout(payload) {
  const res = await fetch(corsUrl('/directory/checkout'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const error = new Error(body?.message || `Directory checkout failed (${res.status})`);
    error.status = res.status;
    throw error;
  }

  return body;
}

/**
 * Live shipping rates for a cart and destination.
 *
 * Only product ids and quantities are sent. Weights, dimensions, and prices all
 * come from the database — the browser never proposes a shipping cost.
 */
export async function fetchShippingRates(payload) {
  const res = await fetch(corsUrl('/shipping/quote'), {
    method: 'POST',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new Error(body?.message || `Could not load shipping rates (${res.status})`);
  }

  return body;
}
